/**
 * Ficha de una cuenta, en drawer lateral.
 *
 * Se monta solo cuando hay una cuenta abierta, así el detalle no se pide hasta
 * que alguien lo mira. Cierra con Escape y con click en el fondo — eso lo trae
 * `Drawer`, que es la primitiva del design system y ya resolvió el foco.
 *
 * Cuando falta un id externo, la ficha no ofrece un formulario de vinculación
 * que no existe: ofrece **disparar el seguimiento** que pide vincularlo, con el
 * título ya escrito. Es la única acción que de verdad puede pasar hoy, y sigue la
 * regla de la casa (el panel dispara trabajo, no lo gestiona).
 *
 * La ficha se abre por URL, así que un id que no existe es un caso normal (un
 * link viejo, un typo). Se responde **dentro del drawer** y no con la 404 de la
 * app: la pantalla que hay detrás — el portfolio — sí existe, y mandar a una 404
 * de página completa haría perder el contexto por un registro que no está.
 */
import {
  CLIENT_STATE_LABELS,
  SEGMENT_LABELS,
  SIN_DATO,
  formatMetricValue,
  formatPeriodo,
  formatRelativeDays,
  type Client,
} from '@ops/contracts'
import { useState, type ReactNode } from 'react'

import { ApiError, mensajeDeError } from '../../lib/api'
import { useClient } from '../../lib/queries'
import { Button, Drawer, EmptyBlock, Rule, Stack, StatusPill } from '../../ui'
import { Cargando, CrearSeguimientoDialog, ErrorDeCarga } from '../comunes'
import { ComentariosCliente } from './ComentariosCliente'
import { FichaActivacion } from './FichaActivacion'
import { SeguimientosCliente } from './SeguimientosCliente'
import { SistemasDeOrigen, type SistemaVinculable } from './SistemasDeOrigen'
import { TimelineEstados } from './TimelineEstados'
import { DIAS_EN_ESTADO_TRABADO, saludEstado } from './derivados'

export interface ClienteDrawerProps {
  clientId: string
  periodo: string
  ahora: Date
  verPlata: boolean
  onClose: () => void
}

export function ClienteDrawer({
  clientId,
  periodo,
  ahora,
  verPlata,
  onClose,
}: ClienteDrawerProps) {
  const detalle = useClient(clientId, periodo)
  const [seguimiento, setSeguimiento] = useState<string | null>(null)
  const cliente = detalle.data?.cliente

  // 404 no es "se rompió la carga": es que el id no corresponde a ninguna
  // cuenta. Son dos hechos distintos y la ficha los dice distinto.
  const noEncontrada =
    detalle.error instanceof ApiError && detalle.error.status === 404

  return (
    <>
      <Drawer
        open
        onClose={onClose}
        width="lg"
        title={
          cliente?.nombre ??
          (noEncontrada ? 'Esa cuenta no existe' : 'Ficha de la cuenta')
        }
        subtitle={
          cliente === undefined ? (
            `Período ${formatPeriodo(periodo)}`
          ) : (
            <span className="flex flex-wrap items-center gap-2">
              <StatusPill severidad={saludEstado(cliente.estado)} size="sm">
                {CLIENT_STATE_LABELS[cliente.estado]}
              </StatusPill>
              <span>
                {cliente.vertical} · {SEGMENT_LABELS[cliente.segmento]} ·{' '}
                {cliente.diasEnEstado} días en estado · último contacto{' '}
                {formatRelativeDays(cliente.ultimoContacto, ahora)}
              </span>
            </span>
          )
        }
        footer={
          cliente === undefined ? undefined : (
            <Button
              variante="primario"
              icono="mas"
              onClick={() =>
                setSeguimiento(`Seguimiento de ${cliente.nombre}`)
              }
            >
              Disparar seguimiento en Plane
            </Button>
          )
        }
      >
        {detalle.isPending ? <Cargando que="la ficha de la cuenta" /> : null}

        {detalle.isError && noEncontrada ? (
          <EmptyBlock
            mensaje={`No hay ninguna cuenta con el id ${clientId}.`}
            detalle={mensajeDeError(detalle.error)}
            icono="buscar"
            punteada
            testId="cliente-no-encontrado"
          >
            <Button variante="secundario" icono="clientes" onClick={onClose}>
              Volver al portfolio
            </Button>
          </EmptyBlock>
        ) : null}

        {detalle.isError && !noEncontrada ? (
          <ErrorDeCarga
            que="la ficha de la cuenta"
            error={detalle.error}
            onRetry={() => void detalle.refetch()}
            reintentando={detalle.isFetching}
          />
        ) : null}

        {detalle.isSuccess && cliente !== undefined ? (
          <Stack gap="md">
            <Bloque titulo="Cuenta">
              <DatosCuenta cliente={cliente} ahora={ahora} verPlata={verPlata} />
            </Bloque>

            <Bloque titulo="Activación">
              <FichaActivacion
                nombre={cliente.nombre}
                activacion={cliente.activacion}
                agentes={detalle.data.agentes}
                links={detalle.data.links.agentes}
              />
            </Bloque>

            <Bloque titulo="Sistemas de origen">
              <SistemasDeOrigen
                links={detalle.data.links.clientes[cliente.id]}
                nombre={cliente.nombre}
                verPlata={verPlata}
                orientacion="columna"
                conSuscripcion
                onVincular={(sistema: SistemaVinculable) =>
                  setSeguimiento(`Vincular ${sistema} de ${cliente.nombre}`)
                }
              />
            </Bloque>

            <Bloque titulo="Historial de estados">
              <TimelineEstados
                cliente={cliente}
                ahora={ahora}
                links={detalle.data.links.clientes[cliente.id]}
              />
            </Bloque>

            <Bloque titulo="Seguimientos · viven en Plane">
              <SeguimientosCliente
                followups={detalle.data.followups}
                links={detalle.data.links.followups}
                onCrear={() =>
                  setSeguimiento(`Seguimiento de ${cliente.nombre}`)
                }
              />
            </Bloque>

            <Bloque titulo="Comentarios del equipo" ultimo>
              <ComentariosCliente
                clientId={cliente.id}
                clientNombre={cliente.nombre}
                ahora={ahora}
              />
            </Bloque>
          </Stack>
        ) : null}
      </Drawer>

      {seguimiento !== null && cliente !== undefined ? (
        <CrearSeguimientoDialog
          open
          onClose={() => setSeguimiento(null)}
          entityType="client"
          entityId={cliente.id}
          tituloInicial={seguimiento}
          contexto={`Abierto desde el módulo de Clientes del panel. Contexto: ${cliente.nombre} · ${CLIENT_STATE_LABELS[cliente.estado]}.`}
          nombre={`${cliente.nombre} · ${CLIENT_STATE_LABELS[cliente.estado]}`}
          // La ficha lee sus seguimientos de `/api/clients/:id`, que
          // `useCreateFollowup` no invalida: sin esto la lista queda vieja.
          invalidar={[['client']]}
        />
      ) : null}
    </>
  )
}

interface BloqueProps {
  titulo: string
  ultimo?: boolean
  children: ReactNode
}

/** Bloque de la ficha. Rótulo chico y regla: el drawer es denso, no una landing. */
function Bloque({ titulo, ultimo, children }: BloqueProps) {
  return (
    <section aria-label={titulo}>
      <h3 className="rotulo mb-2">{titulo}</h3>
      {children}
      {ultimo === true ? null : <Rule spacing="none" className="mt-4" />}
    </section>
  )
}

interface DatosCuentaProps {
  cliente: Client
  ahora: Date
  verPlata: boolean
}

function DatosCuenta({ cliente, ahora, verPlata }: DatosCuentaProps) {
  const items: { rotulo: string; valor: ReactNode }[] = []

  if (verPlata) {
    items.push({
      rotulo: 'MRR',
      valor:
        cliente.mrr === null ? SIN_DATO : formatMetricValue(cliente.mrr, 'usd'),
    })
  }

  items.push(
    { rotulo: 'Dueño', valor: cliente.owner?.nombre ?? 'Sin dueño asignado' },
    {
      rotulo: 'Días en estado',
      valor: (
        <span
          className={
            cliente.diasEnEstado >= DIAS_EN_ESTADO_TRABADO
              ? 'text-danger-text'
              : undefined
          }
        >
          {formatMetricValue(cliente.diasEnEstado, 'count')}
        </span>
      ),
    },
    {
      rotulo: 'Último contacto',
      valor: formatRelativeDays(cliente.ultimoContacto, ahora),
    },
    {
      rotulo: 'Prioridad SEO',
      valor: <span className="capitalize">{cliente.prioridadSeo}</span>,
    },
  )

  return (
    <dl className="grid grid-cols-2 gap-3">
      {items.map((item) => (
        <div key={item.rotulo} className="min-w-0">
          <dt className="rotulo">{item.rotulo}</dt>
          <dd className="tabular text-titulo font-semibold text-text">
            {item.valor}
          </dd>
        </div>
      ))}
    </dl>
  )
}
